import { useCallback, useEffect, useState } from 'react';
import type { DrivingChallengeId } from '../data/challengeOptions';
import {
  createEmptyDrivingRecordBook,
  getStoredChallengeBest,
  getStoredLapBest,
  readDrivingRecordBook,
  withChallengeResult,
  withLapResult,
  writeDrivingRecordBook,
  type DrivingRecordBook,
} from '../model/drivingStorage';

export interface DrivingRecordsController {
  records: DrivingRecordBook;
  getLapBest: (vehicleId: string, courseId: string) => number | null;
  getChallengeBest: (vehicleId: string, challengeId: DrivingChallengeId) => number | null;
  saveLapResult: (vehicleId: string, courseId: string, resultSeconds: number) => void;
  saveChallengeResult: (
    vehicleId: string,
    challengeId: DrivingChallengeId,
    result: number
  ) => void;
  clearRecords: () => void;
}

/**
 * 베스트 기록을 차량별로 저장합니다.
 *
 * 기록 갱신은 랩/챌린지 완료 때만 오므로 React state여도 프레임 성능에 영향이 없습니다.
 * localStorage 쓰기는 커밋 뒤 effect에서 하며, 저장 실패는 model/drivingStorage가 삼킵니다.
 */
export function useDrivingRecords(): DrivingRecordsController {
  const [records, setRecords] = useState<DrivingRecordBook>(readDrivingRecordBook);

  useEffect(() => {
    writeDrivingRecordBook(records);
  }, [records]);

  const getLapBest = useCallback(
    (vehicleId: string, courseId: string) => getStoredLapBest(records, vehicleId, courseId),
    [records]
  );
  const getChallengeBest = useCallback(
    (vehicleId: string, challengeId: DrivingChallengeId) =>
      getStoredChallengeBest(records, vehicleId, challengeId),
    [records]
  );
  const saveLapResult = useCallback(
    (vehicleId: string, courseId: string, resultSeconds: number) => {
      setRecords((current) => withLapResult(current, vehicleId, courseId, resultSeconds));
    },
    []
  );
  const saveChallengeResult = useCallback(
    (vehicleId: string, challengeId: DrivingChallengeId, result: number) => {
      setRecords((current) => withChallengeResult(current, vehicleId, challengeId, result));
    },
    []
  );
  const clearRecords = useCallback(() => setRecords(createEmptyDrivingRecordBook()), []);

  return {
    records,
    getLapBest,
    getChallengeBest,
    saveLapResult,
    saveChallengeResult,
    clearRecords,
  };
}
